import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Injectable, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { BehaviorSubject, Subscription, Observable, of } from 'rxjs';
import { WorkFlowService } from 'src/app/components/workflow/workflow.service';
import { WFStatus, ReorderResource, WFTableConfig } from '../workflow';
import { DocumentCategory, DocumentType } from 'src/app/services/documents/documents';
import { BaseComponent } from 'src/app/common/base/base.component';

/**
 * WF node data with nested structure.
 * Each node has a name, and a type or a list of children.
 */
export class WFNode {
  idx!: string;
  children?: WFNode[];
  name!: string;
  type: any;
  id!: number;
  level!: number;
  order!: number;
  icon!: string;
  tableId!: number;
  sectionId?: number;
  stepId?: number;
  taskId?: number;
  actionId?: number;
  roleId?: number;
  parentIdx?: string;
  status?: number;
}

/** Flat node with expandable and level information */
export class WFFlatNode {
  constructor(
    public expandable: boolean,
    public name: string,
    public level: number,
    public type: any,
    public idx: string,
    public id: number,
    public order: number,
    public icon: string,
    public tableId: number,
    public sectionId: number,
    public stepId: number,
    public taskId: number,
    public actionId: number,
    public roleId: number,
    public parentIdx: string,
    public status: number
  ) { }
}

/**
 * WF database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has name and type.
 * For a directory, it has name and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `WFNode` with nested
 * structure.
 */
@Injectable()
export class WFData {

  get data(): WFNode[] { return this.dataChange.value; }

  constructor(private service: WorkFlowService) {
    this.initialize();
  }
  dataChange = new BehaviorSubject<WFNode[]>([]);
  wfTableConfigs = new BehaviorSubject<WFTableConfig[]>([]);

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `WFNode`.
   */

  nodes: WFNode[] = [];
  tableId!: number;
  sectionId?: number;
  stepId?: number;
  taskId?: number;
  actionId?: number;
  roleId?: number;
  wfStatuses: any[] = this.getENUM(WFStatus);
  status?: number;

  async initialize(formType: (DocumentType | null) = null) {
    // Parse the string to json object.
    // const dataObject = JSON.parse(TREE_DATA);
    const dataObject = null;

    if (formType) {
      const wf = await this.service.getWFTableByForm(formType.id).toPromise();
      const data = this.buildWFTree(wf ?? [], 0);
      this.dataChange.next(data);
      this.wfTableConfigs.next(wf ?? []);
    }

  }

  buildWFTree(obj: any[], level: number, parentId: string = '0') {
    let data: WFNode[] = [];
    let idx = 0;
    if (obj != null) {
      obj.forEach(t => {

        let name = t.sectionName ? t.sectionName : t.name;

        if (name != '' && name != undefined && name != null) {
          let node: WFNode = new WFNode();

          node.id = t.id;
          node.name = t.name ? t.name : t.sectionName;
          node.idx = `${parentId}/${idx}`;
          node.level = level;
          node.order = idx + 1;
          node.parentIdx = parentId;

          switch (level) {
            case 0:
              this.tableId = t.id;
              node.name = t.name + (t.status == 1 ? '     [' + this.wfStatuses.find(s => s.value == t.status).key + ']' : '');
              node.icon = 'play_circle_filled';
              node.children = this.buildWFTree(t.wfSectionConfigs, level + 1, node.idx);
              this.status = t.status;
              break;
            case 1:
              this.sectionId = t.id;
              node.name = t.name;
              node.icon = 'view_list';
              node.children = this.buildWFTree(t.wfStepConfigs, level + 1, node.idx);
              break;
            case 2:
              this.stepId = t.id;
              node.name = t.name;
              node.icon = 'view_column';
              node.children = this.buildWFTree(t.wfTaskConfigs, level + 1, node.idx);
              break;
            case 3:
              this.taskId = t.id;
              node.name = t.name;
              node.icon = 'assignment';

              let node_a: WFNode = new WFNode();
              node_a.id = 0;
              node_a.idx = `${node.idx}/${node_a.id}`;
              node_a.name = 'Actions';
              node_a.type = 11;
              node_a.level = 4;
              node_a.tableId = this.tableId;
              node_a.sectionId = this.sectionId;
              node_a.stepId = this.stepId;
              node_a.taskId = this.taskId;
              node_a.actionId = this.actionId;
              node_a.roleId = this.roleId;
              node_a.status = this.status;
              node_a.icon = 'touch_app';
              node_a.children = this.buildWFTree(t.actionConfigs, node_a.level + 1, node.idx);

              let node_b: WFNode = new WFNode();
              node_b.id = 1;
              node_b.idx = `${node.idx}/${node_b.id}`;
              node_b.name = 'Roles';
              node_b.type = 12;
              node_b.level = 4;
              node_b.tableId = this.tableId;
              node_b.sectionId = this.sectionId;
              node_b.stepId = this.stepId;
              node_b.taskId = this.taskId;
              node_b.actionId = this.actionId;
              node_b.roleId = this.roleId;
              node_b.status = this.status;
              node_b.icon = 'people_alt';
              node_b.children = this.buildWFTree(t.roleConfigs, node_b.level + 1, node.idx);

              let nodes: WFNode[] = [];
              nodes.push(node_a);
              nodes.push(node_b);
              node.children = nodes;
              break;
            case 5:
              if (t.action != undefined) {
                node.type = 11;
                node.icon = 'assignment';
                this.actionId = t.id;
                node.name = t.name;
              }
              else {
                node.type = 12;
                node.icon = 'people_alt';
                this.roleId = t.id;
                node.name = t.roleName;
              }

              break;

          }

          node.tableId = this.tableId;
          node.sectionId = this.sectionId;
          node.stepId = this.stepId;
          node.taskId = this.taskId;
          node.actionId = this.actionId;
          node.roleId = this.roleId;
          node.status = this.status;

          data.push(node);
          idx++;
        }

      });
    }

    return data;
  }


  getENUM(ENUM: any): string[] {
    const myEnum: any[] = [];
    const objectEnum = Object.entries(ENUM);
    objectEnum.forEach(e => {
      if (typeof e[1] === 'number') {
        myEnum.push({ key: e[0], value: e[1] });
      }
    });
    return myEnum;
  }

}


@Component({
  selector: 'workflow-tree',
  templateUrl: './workflow-tree.component.html',
  styleUrls: ['./workflow-tree.component.scss'],
  providers: [WFData]
})
/** workflow-tree component*/
export class WorkflowTreeComponent extends BaseComponent implements OnInit, OnDestroy {

  @Input() creating?: boolean;
  @Input() editing?: boolean;

  @Output() nodeSelected = new EventEmitter();

  documentType?: DocumentType;
  documentTypes?: DocumentType[];
  documentTypesFiltered?: DocumentType[];
  documentTypes$!: Observable<DocumentType[]>;
  documentTypesSubs!: Subscription;

  treeControl: FlatTreeControl<WFFlatNode>;
  treeFlattener: MatTreeFlattener<WFNode, WFFlatNode>;
  dataSource: MatTreeFlatDataSource<WFNode, WFFlatNode>;
  expandedNodeSet = new Set<string>();
  dragging = false;
  expandTimeout: any;
  expandDelay = 1000;
  activeNode?: WFNode;
  expandedNodes?: WFNode[];
  currentNode?: WFNode;
  typeCtrl = new FormControl();
  // Booleans
  tableId?: number;
  allowNew?: boolean;

  wfTableConfigs?: WFTableConfig[];

  transformer = (node: WFNode, level: number) => {
    return new WFFlatNode(!!node.children, node.name, level, node.type, node.idx, node.id, node.order, node.icon, node.tableId, node.sectionId ?? 0, node.stepId ?? 0, node.taskId ?? 0, node.actionId ?? 0, node.roleId ?? 0, node.parentIdx ?? '', node.status ?? 0);
  }
  private _getLevel = (node: WFFlatNode) => node.level;
  private _isExpandable = (node: WFFlatNode) => node.expandable;
  private _getChildren = (node: WFNode): Observable<WFNode[]> => of(node.children ?? []);

  hasChild = (_: number, _nodeData: WFFlatNode) => _nodeData.expandable;

  constructor(
    protected override injector: Injector,
    private database: WFData,
    private service: WorkFlowService,
  ) {
    super(injector);
    this.treeFlattener = new MatTreeFlattener(this.transformer, this._getLevel, this._isExpandable, this._getChildren);
    this.treeControl = new FlatTreeControl<WFFlatNode>(this._getLevel, this._isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    database.dataChange.subscribe(data => {
      this.rebuildTreeForData(data);
      // this.wfTableConfigs = data;
    });

    database.wfTableConfigs.subscribe(data => {
      this.wfTableConfigs = data;
    });

  }

  ngOnInit() {
    this.allowNew = this.currentUser != null && this.checkRoles();
    // this.loading = true;
    // this.load();
    this.loadDocumentTypes();
  }

  override ngOnDestroy(): void {
    this.documentTypesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  loadDocumentTypes() {
    this.documentTypes$ = this.store.select(state => state.DocumentType.data);
    this.documentTypesSubs = this.documentTypes$.subscribe(data => {
      if (data?.length) {
        this.documentTypes = data.filter(d => d.category == DocumentCategory.Form).sort((a, b) => this.utils.sort(a.name, b.name));
        this.documentTypesFiltered = this.documentTypes;
      }
    });
  }

  rebuildTree(id?: number) {
    const event = { option: { value: this.documentType } } as MatAutocompleteSelectedEvent;
    this.selectedType(event).then(() => {
      if (id) {
        this.tableId = id;
        const node = this.dataSource.data.find(n => n.tableId === id);
        if (node) {
          this.nodeClicked(node);
          this.refresh(node);
        } else {
          this.nodeSelected.emit(undefined);
        }
      }
    });
  }

  async selectedType(event: MatAutocompleteSelectedEvent) {
    this.service.loading = true;
    this.cdRef.detectChanges();
    this.documentType = event.option.value;
    this.dataSource.data = [];
    const node = new WFNode();
    node.idx = '0';
    node.tableId = 0;
    node.level = -1;
    this.activeNode = undefined;
    this.expandedNodeSet = new Set<string>();
    this.expandedNodes = [];
    this.nodeSelected.emit(undefined);
    await this.database.initialize(event.option.value);
    this.setHeights();
  }

  async refresh(e: WFNode) {
    this.currentNode = e;
    this.activeNode = e;
    await this.refreshTree(e);
  }

  nodeClicked(node?: WFNode) {
    this.tableId = node?.tableId;
    this.activeNode = node;
    this.nodeSelected.emit(node);
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
  }

  checkRoles() {
    return true;
  }

  async refreshTree(node: WFNode) {
    this.service.loading = true;
    await this.database.initialize(this.typeCtrl.value);
    this.editing = false;
    this.nodeClicked(node);
  }

  // saveExpandedNodes() {
  //  this.expandedNodes = new Array<WFNode>();
  //  this.treeControl.dataNodes.forEach(node => {
  //    if (node.expandable && this.treeControl.isExpanded(node)) {
  //      this.expandedNodes.push(node);
  //    }
  //  });
  // }

  // restoreExpandedNodes() {
  //  this.expandedNodes.forEach(node => {
  //    this.treeControl.expand(this.treeControl.dataNodes.find(n => n.idx === node.idx));
  //  });
  // }

  /**
   * This constructs an array of nodes that matches the DOM,
   * and calls rememberExpandedTreeNodes to persist expand state
   */
  visibleNodes(): WFNode[] {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    const result: WFNode[] = [];

    function addExpandedChildren(node: WFNode, expanded: Set<string>) {
      result.push(node);
      if (expanded.has(node.idx)) {
        node.children?.map(child => addExpandedChildren(child, expanded));
      }
    }
    this.dataSource.data.forEach(node => {
      addExpandedChildren(node, this.expandedNodeSet);
    });
    return result;
  }

  /**
   * Handle the drop - here we rearrange the data based on the drop event,
   * then rebuild the tree.
   * */
  drop(event: CdkDragDrop<string[]>) {
    // console.log('origin/destination', event.previousIndex, event.currentIndex);

    // ignore drops outside of the tree
    if (!event.isPointerOverContainer) { return; }

    // construct a list of visible nodes, this will match the DOM.
    // the cdkDragDrop event.currentIndex jives with visible nodes.
    // it calls rememberExpandedTreeNodes to persist expand state
    const visibleNodes = this.visibleNodes();

    // deep clone the data source so we can mutate it
    const changedData = JSON.parse(JSON.stringify(this.dataSource.data));

    // recursive find function to find siblings of node
    function findNodeSiblings(arr: Array<any>, idx: string): Array<any> {
      let result: any[] = [], subResult;
      arr.forEach(item => {
        if (item.idx === idx) {
          result = arr;
        } else if (item.children) {
          subResult = findNodeSiblings(item.children, idx);
          if (subResult) { result = subResult; }
        }
      });
      return result;
    }

    // remove the node from its old place
    const node = event.item.data;
    const siblings = findNodeSiblings(changedData, node.idx);
    const siblingIndex = siblings.findIndex(n => n.idx === node.idx);
    const nodeToInsert: WFNode = siblings.splice(siblingIndex, 1)[0];

    // determine where to insert the node
    const nodeAtDest = visibleNodes[event.currentIndex];
    if (nodeAtDest.idx === nodeToInsert.idx || nodeAtDest.parentIdx != nodeToInsert.parentIdx) { return; }

    // determine drop index relative to destination array
    let relativeIndex = event.currentIndex; // default if no parent
    const nodeAtDestFlatNode = this.treeControl.dataNodes.find(n => nodeAtDest.idx === n.idx)!;
    const parent = this.getParentNode(nodeAtDestFlatNode);
    if (parent) {
      const parentIndex = visibleNodes.findIndex(n => n.idx === parent.idx) + 1;
      relativeIndex = event.currentIndex - parentIndex;
    }
    // insert node
    const newSiblings = findNodeSiblings(changedData, nodeAtDest.idx);
    if (!newSiblings) { return; }
    newSiblings.splice(relativeIndex, 0, nodeToInsert);


    this.updateDb(newSiblings);

    // rebuild tree with mutated data
    this.rebuildTreeForData(changedData);

  }

  /**
   * Experimental - opening tree nodes as you drag over them
   */
  dragStart() {
    this.dragging = true;
  }
  dragEnd() {
    this.dragging = false;
  }
  dragHover(node: WFFlatNode) {

    if (this.dragging) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = setTimeout(() => {
        this.treeControl.expand(node);
      }, this.expandDelay);
    }
  }
  dragHoverEnd() {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
    }
  }

  /**
   * The following methods are for persisting the tree expand state
   * after being rebuilt
   */

  rebuildTreeForData(data: any) {
    this.treeControl.collapseAll();
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    this.dataSource.data = data;
    this.forgetMissingExpandedNodes(this.treeControl, this.expandedNodeSet);
    this.expandNodesById(this.treeControl.dataNodes, Array.from(this.expandedNodeSet));
    this.service.loading = false;
  }

  private rememberExpandedTreeNodes(
    treeControl: FlatTreeControl<WFFlatNode>,
    expandedNodeSet: Set<string>
  ) {
    if (treeControl.dataNodes) {
      treeControl.dataNodes.forEach((node) => {
        if (treeControl.isExpandable(node) && treeControl.isExpanded(node)) {
          // capture latest expanded state
          expandedNodeSet.add(node.idx);
        }
      });
    }
  }

  private forgetMissingExpandedNodes(
    treeControl: FlatTreeControl<WFFlatNode>,
    expandedNodeSet: Set<string>
  ) {
    if (treeControl.dataNodes) {
      expandedNodeSet.forEach((nodeId) => {
        // maintain expanded node state
        if (!treeControl.dataNodes.find((n) => n.idx === nodeId)) {
          // if the tree doesn't have the previous node, remove it from the expanded list
          expandedNodeSet.delete(nodeId);
        }
      });
    }
  }

  private expandNodesById(flatNodes: WFFlatNode[], ids: string[]) {
    if (!flatNodes || flatNodes.length === 0) { return; }
    const idSet = new Set(ids);
    return flatNodes.forEach((node) => {
      if (idSet.has(node.idx)) {
        this.treeControl.expand(node);
        // let parent = this.getParentNode(node);
        // while (parent) {
        //  this.treeControl.expand(parent);
        //  parent = this.getParentNode(parent);
        // }
      }
    });
  }

  private getParentNode(node: WFFlatNode): WFFlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  private updateDb(siblings: WFNode[]) {

    const resources: ReorderResource[] = [];
    const level = siblings[0].level;
    let id: number;
    let order = 0;


    siblings.forEach(s => {
      order++;

      switch (level) {
        case 1:
          id = s.sectionId ?? 0;
          break;

        case 2:
          id = s.stepId ?? 0;
          break;

        case 3:
          id = s.taskId ?? 0;
          break;
      }

      const res: ReorderResource = {
        id,
        order
      };
      resources.push(res);
    });

    this.service.reorderItems(resources, level).subscribe(data => { });

  }
}
